import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import Breadcrumb from '@/components/base/Breadcrumb';
import { aboutInfo } from '@/mocks/about';

export default function About() {
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = (formData.get('company_alt') as string || '').trim();
    if (honeypot) {
      setFormStatus('success');
      return;
    }

    setFormStatus('submitting');
    setFormError('');

    try {
      formData.delete('company_alt');
      const params = new URLSearchParams();
      formData.forEach((value, key) => {
        params.append(key, value as string);
      });

      const response = await fetch('https://readdy.ai/api/form/d9q9crnnahb7382ov850', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });

      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = { code: '' }; }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
        setFormError(serverMsg || '提交失败，请稍后重试');
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请检查网络后重试');
      setFormStatus('error');
    }
  };

  return (
    <>
      <ScrollToTop />
      <Navbar />
      <main>
        <section className="pt-24 pb-8 bg-background-100">
          <div className="w-full max-w-7xl mx-auto px-4 md:px-6">
            <Breadcrumb items={[{ label: '关于我们' }]} />
            <h1 className="font-heading text-3xl md:text-4xl font-bold text-foreground-900 mt-4">
              关于磁力金牛
            </h1>
          </div>
        </section>

        <section className="py-16 bg-background-50">
          <div className="w-full max-w-7xl mx-auto px-4 md:px-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-6">
                  {aboutInfo.brandSlogan}
                </h2>
                <div className="text-foreground-600 leading-relaxed space-y-4 text-sm">
                  {aboutInfo.brandStory.split('\n\n').map((p, i) => (
                    <p key={i}>{p}</p>
                  ))}
                </div>
              </div>
              <div className="aspect-[4/3] rounded-xl overflow-hidden bg-background-100">
                <img
                  src="https://readdy.ai/api/search-image?query=Modern%20luxurious%20office%20space%20with%20warm%20golden%20lighting%2C%20minimalist%20design%2C%20abstract%20art%20on%20walls%2C%20professional%20financial%20atmosphere%2C%20elegant%20composition%20with%20soft%20shadows&width=800&height=600&seq=about-brand&orientation=landscape"
                  alt="磁力金牛品牌形象"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        </section>

        <section className="py-16 bg-background-100">
          <div className="w-full max-w-7xl mx-auto px-4 md:px-6">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 text-center mb-12">
              核心价值观
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {aboutInfo.coreValues.map((v) => (
                <div key={v.title} className="bg-background-50 rounded-xl p-6 border border-background-200/70 text-center">
                  <div className="w-14 h-14 mx-auto mb-4 flex items-center justify-center rounded-2xl bg-primary-100 text-primary-600">
                    <i className={`${v.icon} text-2xl`}></i>
                  </div>
                  <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-2">{v.title}</h3>
                  <p className="text-sm text-foreground-500 leading-relaxed">{v.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-16 bg-background-50">
          <div className="w-full max-w-7xl mx-auto px-4 md:px-6">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 text-center mb-12">
              发展历程
            </h2>
            <div className="max-w-3xl mx-auto">
              <div className="relative">
                <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-px bg-background-300/60 -translate-x-1/2"></div>
                <div className="space-y-8">
                  {aboutInfo.milestones.map((m, i) => (
                    <div key={i} className={`relative flex items-start gap-6 ${i % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'}`}>
                      <div className="flex-1"></div>
                      <div className="relative z-10 w-8 h-8 flex-shrink-0 flex items-center justify-center rounded-full bg-primary-500 text-background-50">
                        <div className="w-3 h-3 rounded-full bg-background-50"></div>
                      </div>
                      <div className="flex-1 bg-background-100 rounded-xl p-5 border border-background-200/70">
                        <div className="font-heading font-semibold text-primary-600 mb-1">{m.year}</div>
                        <p className="text-sm text-foreground-600">{m.event}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="py-16 bg-background-100">
          <div className="w-full max-w-7xl mx-auto px-4 md:px-6">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 text-center mb-12">
              用户口碑
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {aboutInfo.testimonials.map((t, i) => (
                <div key={i} className="bg-background-50 rounded-xl p-6 border border-background-200/70">
                  <div className="flex items-center gap-1 mb-3">
                    {Array.from({ length: 5 }).map((_, j) => (
                      <i key={j} className={`text-sm ${j < t.rating ? 'ri-star-fill text-primary-500' : 'ri-star-line text-foreground-300'}`}></i>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed mb-4">{t.content}</p>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-accent-100 flex items-center justify-center">
                      <i className="ri-user-3-line text-accent-600"></i>
                    </div>
                    <div>
                      <div className="font-heading text-sm font-semibold text-foreground-900">{t.name}</div>
                      <div className="text-xs text-foreground-400">{t.role}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-16 bg-background-50" id="contact">
          <div className="w-full max-w-7xl mx-auto px-4 md:px-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
              <div>
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-6">
                  联系我们
                </h2>
                <div className="space-y-5">
                  <div className="flex items-start gap-4">
                    <div className="w-11 h-11 flex items-center justify-center rounded-xl bg-primary-100 text-primary-600 flex-shrink-0">
                      <i className="ri-phone-line text-lg"></i>
                    </div>
                    <div>
                      <div className="font-heading font-semibold text-foreground-900">客服热线</div>
                      <div className="text-sm text-foreground-500">{aboutInfo.contact.phone}</div>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-11 h-11 flex items-center justify-center rounded-xl bg-primary-100 text-primary-600 flex-shrink-0">
                      <i className="ri-mail-line text-lg"></i>
                    </div>
                    <div>
                      <div className="font-heading font-semibold text-foreground-900">电子邮箱</div>
                      <div className="text-sm text-foreground-500">{aboutInfo.contact.email}</div>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-11 h-11 flex items-center justify-center rounded-xl bg-primary-100 text-primary-600 flex-shrink-0">
                      <i className="ri-map-pin-line text-lg"></i>
                    </div>
                    <div>
                      <div className="font-heading font-semibold text-foreground-900">公司地址</div>
                      <div className="text-sm text-foreground-500">{aboutInfo.contact.address}</div>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-11 h-11 flex items-center justify-center rounded-xl bg-primary-100 text-primary-600 flex-shrink-0">
                      <i className="ri-time-line text-lg"></i>
                    </div>
                    <div>
                      <div className="font-heading font-semibold text-foreground-900">工作时间</div>
                      <div className="text-sm text-foreground-500">{aboutInfo.contact.workingHours}</div>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-11 h-11 flex items-center justify-center rounded-xl bg-primary-100 text-primary-600 flex-shrink-0">
                      <i className="ri-wechat-line text-lg"></i>
                    </div>
                    <div>
                      <div className="font-heading font-semibold text-foreground-900">官方微信</div>
                      <div className="text-sm text-foreground-500">{aboutInfo.contact.wechat}</div>
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-6">
                  在线留言
                </h2>
                <form onSubmit={handleSubmit} data-readdy-form className="bg-background-100 rounded-xl p-6 border border-background-200/70 space-y-4">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-foreground-700 mb-1.5">姓名</label>
                    <input
                      type="text" id="name" name="name" required
                      className="w-full px-4 py-2.5 rounded-lg border border-background-300/60 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-colors"
                      placeholder="请输入您的姓名"
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-foreground-700 mb-1.5">邮箱</label>
                    <input
                      type="email" id="email" name="email" required
                      className="w-full px-4 py-2.5 rounded-lg border border-background-300/60 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-colors"
                      placeholder="请输入您的邮箱"
                    />
                  </div>
                  <div>
                    <label htmlFor="phone" className="block text-sm font-medium text-foreground-700 mb-1.5">手机号</label>
                    <input
                      type="tel" id="phone" name="phone"
                      className="w-full px-4 py-2.5 rounded-lg border border-background-300/60 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-colors"
                      placeholder="请输入您的手机号"
                    />
                  </div>
                  <div>
                    <label htmlFor="message" className="block text-sm font-medium text-foreground-700 mb-1.5">留言内容</label>
                    <textarea
                      id="message" name="message" required rows={4} maxLength={500}
                      className="w-full px-4 py-2.5 rounded-lg border border-background-300/60 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-colors resize-none"
                      placeholder="请输入您想咨询的内容（最多500字）"
                    ></textarea>
                  </div>

                  <div className="form-honeypot">
                    <input type="text" name="company_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />
                  </div>

                  {formStatus === 'success' && (
                    <div className="p-3 rounded-lg bg-accent-100 text-accent-800 text-sm">
                      提交成功！我们会尽快与您联系，感谢您的留言。
                    </div>
                  )}
                  {formStatus === 'error' && (
                    <div className="p-3 rounded-lg bg-red-50 text-red-700 text-sm">
                      {formError || '提交失败，请稍后重试'}
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={formStatus === 'submitting'}
                    className="w-full px-6 py-3 bg-primary-500 hover:bg-primary-600 disabled:opacity-60 text-background-50 font-medium rounded-lg text-sm transition-colors whitespace-nowrap"
                  >
                    {formStatus === 'submitting' ? '提交中...' : '提交留言'}
                  </button>
                </form>
              </div>
            </div>
          </div>
        </section>

        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'AboutPage',
          name: '关于磁力金牛',
          description: aboutInfo.brandSlogan,
        }) }} />
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: '首页', item: 'https://www.cicjinni.com/' },
            { '@type': 'ListItem', position: 2, name: '关于我们', item: 'https://www.cicjinni.com/about' },
          ],
        }) }} />
      </main>
      <Footer />
    </>
  );
}